<script setup lang="ts">
import { computed, ref } from 'vue'
import qs from 'qs'
import Filters from '@/components/filters/Filters.vue'
import { type FilterDefinition } from '@/components/filters/filters'
import { Button } from '@/shadcn/components/ui/button'
import Icon from '@/components/ui/icon/Icon.vue'

const props = defineProps<{
  availableFilters: FilterDefinition[]
  downloadUrl: string
}>()

const filters = ref<Record<string, string | boolean>>(initialFilters())

function initialFilters(): Record<string, string | boolean> {
  const parsed = qs.parse(window.location.search, { ignoreQueryPrefix: true })
  const fromUrl = (parsed.filter as Record<string, string> | undefined) ?? {}

  return props.availableFilters.reduce<Record<string, string | boolean>>((acc, filter) => {
    const value = fromUrl[filter.name] ?? filter.default
    if (value !== undefined && value !== '') {
      acc[filter.name] = value
    }
    return acc
  }, {})
}

const exportHref = computed(() => {
  const query = qs.stringify({ filter: filters.value }, { encode: false, skipNulls: true })
  return query ? `${props.downloadUrl}?${query}` : props.downloadUrl
})
</script>

<template>
  <div class="tw:flex tw:flex-col tw:gap-4">
    <section class="card">
      <div class="card-body">
        <h5 class="card-title tw:mb-1">{{ $t('Step 1 — Configure filters') }}</h5>
        <p class="text-muted tw:mb-3">
          {{ $t('Refine the data range to include in your export.') }}
        </p>
        <Filters
          v-model="filters"
          :available-filters="availableFilters"
          :base-filters="{}"
        />
      </div>
    </section>

    <section class="card">
      <div class="card-body tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:md:justify-between tw:gap-3">
        <div>
          <h5 class="card-title tw:mb-1">{{ $t('Step 2 — Download the export') }}</h5>
          <p class="text-muted tw:mb-0">
            {{ $t('You will receive an email when the file is ready.') }}
          </p>
        </div>
        <Button as-child variant="outline">
          <a :href="exportHref">
            <Icon name="Upload" class="tw:w-3 tw:h-3" />
            {{ $t('Export to Excel format') }}
          </a>
        </Button>
      </div>
    </section>
  </div>
</template>
